'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';

import { UiKitBannerHero } from '@/fo/ui/kit/banner/hero';
import { UiKitBreadcrumbsBase } from '@/fo/ui/kit/breadcrumbs/base';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import type { FilterOption } from '@/fo/ui/product/layout/catalog';

import type {
  ArticlesPageHeroBanner,
  GetArticlesByTypeResult
} from '../../utils/getArticlesByType';
import { getArticlesByType } from '../../utils/getArticlesByType';
import { ArticleTypeSort } from '../ArticleTypeSort';

import { styles } from './ArticlePageContent.styles';

const TYPE_FILTER_VALUES = ['', 'news', 'promo'] as const;
const TYPE_FILTER_KEYS = [
  'article.page.typeFilter.all',
  'article.page.typeFilter.news',
  'article.page.typeFilter.promo'
] as const;

type PageDoc = GetArticlesByTypeResult['docs'][number];

/** Формат даты dd.mm.yyyy */
function formatDateDDMMYYYY(dateStr: string): string {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function getArticleDate(item: PageDoc): string | undefined {
  const type = item.type;
  if (type === 'news' && item.createdAt) {
    return formatDateDDMMYYYY(item.createdAt);
  }
  if (type === 'promo' && item.promoDateExpire) {
    // eslint-disable-next-line i18n/no-russian-character -- префикс даты акции
    return `от ${formatDateDDMMYYYY(item.promoDateExpire)}`;
  }
  return undefined;
}

export interface ArticlePageContentProps {
  initialDocs: PageDoc[];
  initialHasNextPage: boolean;
  heroBanners?: ArticlesPageHeroBanner[];
  buttonText: string;
}

export function ArticlePageContent({
  initialDocs,
  initialHasNextPage,
  heroBanners = [],
  buttonText
}: ArticlePageContentProps) {
  const [selectedType, setSelectedType] = useState('');
  const [docs, setDocs] = useState(initialDocs);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(initialHasNextPage);
  const [page, setPage] = useState(1);
  const slots = styles();
  const t = useTranslations();

  const allSortOptions = useMemo<FilterOption[]>(
    () =>
      TYPE_FILTER_VALUES.map((value, i) => ({
        value,
        label: t(TYPE_FILTER_KEYS[i])
      })),
    [t]
  );

  const onSortChange = useCallback((option: FilterOption | null) => {
    if (!option) return;
    const value = option.value;
    setSelectedType(value);
    const typeFilter = value === '' ? undefined : (value as 'news' | 'promo');
    getArticlesByType(typeFilter, 1).then(
      ({ docs: nextDocs, hasNextPage: nextHasNext }) => {
        setDocs(nextDocs);
        setHasNextPage(nextHasNext);
        setPage(1);
      }
    );
  }, []);

  const onLoadMore = useCallback(() => {
    const nextPage = page + 1;
    const typeFilter =
      selectedType === '' ? undefined : (selectedType as 'news' | 'promo');
    setLoadingMore(true);
    getArticlesByType(typeFilter, nextPage)
      .then(({ docs: nextDocs, hasNextPage: nextHasNext }) => {
        setDocs((prev) => [...prev, ...nextDocs]);
        setHasNextPage(nextHasNext);
        setPage(nextPage);
      })
      .finally(() => {
        setLoadingMore(false);
      });
  }, [page, selectedType]);

  return (
    <div className={slots.base()}>
      <UiKitBreadcrumbsBase
        className={slots.breadcrumbs()}
        items={[
          {
            href: '/',
            label: t('HomePage')
          },
          {
            href: '/articles',
            label: t('News and actions')
          }
        ]}
        small="lg"
        type="dark"
      />

      {heroBanners.length > 0 && (
        <UiKitBannerHero
          type="main"
          className={slots.banner()}
          bannerClassName={slots.bannerContainer()}
          banners={[
            {
              mode: heroBanners[0]?.mode ?? undefined,
              image: {
                sm:
                  typeof heroBanners[0]?.image === 'object' &&
                  heroBanners[0]?.image &&
                  'sizes' in heroBanners[0].image
                    ? (heroBanners[0].image.sizes?.mobile?.url ?? '')
                    : '',
                md:
                  typeof heroBanners[0]?.image === 'object' &&
                  heroBanners[0]?.image &&
                  'sizes' in heroBanners[0].image
                    ? (heroBanners[0].image.sizes?.desktop?.url ?? '')
                    : ''
              },
              title: heroBanners[0]?.title ?? '',
              subtitle: heroBanners[0]?.subtitle ?? '',
              buttonText: heroBanners[0]?.buttonLabel ?? '',
              link: heroBanners[0]?.buttonHref ?? ''
            }
          ]}
        />
      )}

      <ArticleTypeSort
        sortByFilters={allSortOptions}
        selectedSortByFilters={selectedType}
        onSortChange={onSortChange}
        className={slots.sortBlock()}
      />

      <div className={slots.grid()}>
        {docs.map((item) => {
          const imageUrl =
            typeof item.image === 'object' && item.image && 'url' in item.image
              ? (item.image as { url: string }).url
              : '';
          const dateStr = getArticleDate(item);
          return (
            <UiKitBannerHero
              key={item.id}
              type="blog"
              className={slots.articleItem()}
              bannerClassName={slots.articleItemContainer()}
              date={dateStr}
              banners={[
                {
                  mode: 'light',
                  image: { sm: imageUrl, md: imageUrl },
                  title: item.title,
                  subtitle: item?.subtitle ?? '',
                  buttonText,
                  link: item.pathname ?? ''
                }
              ]}
            />
          );
        })}
      </div>

      {hasNextPage && (
        <div className={slots.buttonWrapper()}>
          <UiKitButtonBase
            as="button"
            color="secondary"
            disabled={loadingMore}
            onClick={onLoadMore}
            loading={loadingMore}
          >
            {t('Show more')}
          </UiKitButtonBase>
        </div>
      )}
    </div>
  );
}
